<template>

  <section v-if="rta" class="rta-details padded">

    <!-- XOL-67 (B3), UX v4 section 2 / decision 56: the mail-off banner, RequestWorkflow.vue:44-53 word for word, first in the
         drawer, on an OPEN RTA only (nothing more is sent once it is closed). The switch is for a Tower Co manager and saves at
         once, the way Emails Update does (EmailSetup.onSendMailChange), not on a form pause: an RTA has none. -->
    <el-row v-if="mailBanner" class="rta-mail-off">
      <el-col :span="24">
        <el-alert
          type="info"
          title="This RTA won't send e-mails"
          description=""
          show-icon
          :closable="false">
            In the email settings, send email has been disabled. A manager level user can turn it on.&nbsp;
            <el-switch v-if="canToggleMail" :value="mailOn" aria-label="Send E-mails" @change="onMailSwitch" />
        </el-alert>
      </el-col>
    </el-row>

    <!-- Header: the request's name and the RTA-… reference (decisions 138, 158), the client, the stage and whose turn it is. -->
    <el-row>
      <el-col :span="24" align="center">

        <h4 class="text-warning">
          <small><i class="fa fa-map-pin"></i> Request to Acquire:</small>
          {{ title }}
          <small class="text-muted">| {{ reference }} · {{ suffix }}</small>
        </h4>

        <span v-if="clientBadge">
          <org-badge class="pull-right" :organisation="client" />
        </span>
        <div v-else-if="telcoName" class="rta-client-name">
          <span class="text-muted"><small>by: <b>{{ telcoName }}</b></small></span>
        </div>

        <div class="rta-cue">
          <el-tag size="small" effect="light" :type="stage.type"><i :class="'fa ' + stage.icon" aria-hidden="true"></i> {{ stage.label }}</el-tag>
          <el-tag v-if="siteGone" class="rta-chip" size="mini" effect="light" type="info"><i class="fa fa-link-slash" aria-hidden="true"></i> Site deleted</el-tag>
          <el-tag v-if="showChanged" class="rta-chip" size="mini" effect="light" type="warning"><i class="fa fa-pencil-alt" aria-hidden="true"></i> Changed since submitted</el-tag>

          <span class="rta-cue__line">
            <template v-if="cueLine.kind === 'turn'">
              <el-tag size="mini" effect="light" type="warning"><i class="fa fa-arrow-right" aria-hidden="true"></i> Your turn</el-tag>
              <span>{{ cueLine.text }}</span>
            </template>
            <span v-else-if="cueLine.kind === 'terminal'">{{ cueLine.text }}</span>
            <span v-else>Waiting on: <b>{{ cueLine.name }}</b>{{ cueLine.ending }}</span>
          </span>
        </div>

      </el-col>
    </el-row>

    <!-- The five steps (decision 39). Hidden in a site drawer's record (embedded) and on a narrow screen, where Previous /
         Next Step are the only way between steps. -->
    <el-row v-if="!embedded && (isTablet || isDesktop)">
      <el-col :span="24">
        <span class="rta-visually-hidden">Stage {{ steps.current }} of 5: {{ stage.label }}</span>
        <el-steps :active="activeIndex" finish-status="success" align-center direction="horizontal">
          <el-step
            v-for="(s, i) in stepList"
            :key="s.no"
            :status="stepStatuses[i]"
            :description="i === 4 && steps.terminal === 'rejected' ? 'Not reached' : s.name">
            <template #icon>
              <i :class="'fa ' + s.icon" style="background: inherit;"></i>
            </template>
          </el-step>
        </el-steps>
      </el-col>
    </el-row>

    <!-- The step's title (the RequestWorkflow.vue:175 line). Previous / Next Step are in the step footer at the bottom (decision 130). -->
    <div class="rta-stepline">
      <center><h4 ref="stepHeading" tabindex="-1"><i :class="'fa ' + currentStepMeta.icon"></i> {{ currentStepMeta.name }}</h4> <small class="text-muted">| Step {{ viewStep }} of 5</small></center>
    </div>

    <!-- The Tower Co's notice that a client change cleared its decision (state.rta.lastReset). -->
    <el-alert
      v-if="resetText"
      class="rta-panel rta-reset-notice"
      type="warning"
      show-icon
      :closable="false"
      :title="resetText.title"
      :description="resetText.description" />

    <!-- The status panel: sentences only (UX v3 section 2). Completed adds View site / View RTS on Step 5. -->
    <el-alert
      v-if="panel"
      class="rta-panel rta-status-panel"
      :type="panel.type"
      show-icon
      :closable="false">
      <span slot="title" :id="panelId" class="rta-panel__title" tabindex="-1">{{ panel.title }}</span>
      <p v-if="panel.description" class="rta-panel__description">{{ panel.description }}</p>
      <div v-if="viewStep === 5 && panel.deleted" class="rta-panel__actions">
        <small class="text-muted"><i class="fa fa-link-slash" aria-hidden="true"></i> Site deleted</small>
      </div>
      <div v-else-if="viewStep === 5 && panel.links.length" class="rta-panel__actions">
        <el-button v-if="panel.links.indexOf('site') !== -1 && siteLinkId" type="primary" size="mini" round icon="fa fa-chevron-right" class="rta-view-btn" @click="viewSite">View site</el-button>
        <el-button v-if="panel.links.indexOf('rts') !== -1 && rtsLinkId" type="primary" size="mini" round icon="fa fa-chevron-right" class="rta-view-btn" @click="viewRts">View RTS</el-button>
      </div>
    </el-alert>

    <!-- A Rejected RTA: the reason the Tower Co gave follows the panel (UX spec 2.7), to both roles, on every step. The wrapper is ALWAYS drawn: a node that appears here shifts the siblings below it. -->
    <div class="rta-reject-reason-wrap">
      <read-only-remarks v-if="rejectedReason" class="rta-reject-reason" label="Reason for rejecting" :html="rejectedReason" />
    </div>

    <!-- The step's screen. Step 1 is the request, an inline form that saves field by field (RtaRequest, V3). Steps 2 and 3 are the options (the table, then
         the Map and Files). Step 4 is the Tower Co decision (RtaDecision, rewritten in V5) and Step 5 Finalize (RtaCompletion, V6). A command in an option's
         layer tells this drawer through the `rta-changed` event (onRtaChanged), so the status panel, the stepper and the counts follow. -->
    <div class="rta-step-body" :data-step="viewStep">

      <rta-request
        v-if="viewStep === 1"
        ref="request"
        :rta="rta"
        :options="optionRows"
        :content-version="rowVersion"
        :read-only="embedded || readOnly"
        @open-option="openOption" />

      <!-- Steps 2 and 3: the Options table, then the Map and Files collapses (UX spec 2.2.7). The Map starts open on Step 2 (decision 131) and shows the
           request's crosshairs and every option's pin and extra markers, read-only: a click opens the option, the hover is "Option n". -->
      <div v-else-if="viewStep === 2 || viewStep === 3" class="rta-step-options">
        <site-candidates
          ref="options"
          :site-data="optionSite"
          :action-id="rta.id"
          :rta-data="rta"
          :read-only="embedded || readOnly"
          @loaded="onOptionsLoaded" />

        <el-collapse v-model="openPanels" class="rta-collapse">
          <el-collapse-item name="map">
            <template #title>
              <span class="text-info">Map&nbsp;<i class="header-icon fa fa-map-marker"></i></span>
            </template>
            <!-- The key makes the map again when its panel opens: a Google map made inside a closed (display: none) panel has no size. -->
            <google-map
              v-if="stepMapCentre"
              :key="mapKey"
              :lat="stepMapCentre.lat"
              :lng="stepMapCentre.lng"
              :zoom="stepMapCentre.zoom"
              :pinned="false"
              :pin-markers="stepPins"
              :highlight="stepHighlightKey"
              aspect="16:10"
              width="100%"
              @pin-click="onPinClick"
              @pin-hover="onStepPinHover" />
            <p v-else class="text-muted rta-map-empty">No option has a point yet.</p>
            <!-- Decision 188 (Carlos, 2026-10-29): the Markers table under the Step 2 / 3 map: every pin it draws (the requested point, each option's pin and
                 its extra markers) with Lat., Lng. and the distance; read-only here. A row and its pin highlight each other. -->
            <rta-marker-table
              v-if="stepMapCentre && stepMarkerRows.length"
              :rows="stepMarkerRows"
              :hover-key="stepHoverKey"
              label="Markers of the RTA's options"
              @enter="onStepRowEnter"
              @leave="onStepRowLeave" />
          </el-collapse-item>

          <el-collapse-item name="files">
            <template #title>
              <span class="text-info">Files&nbsp;<i class="header-icon fa fa-paperclip"></i></span>
            </template>
            <!-- DEV-25: the Tower Co (not view-only) writes the RTA's files while it is open; everyone else reads. -->
            <files-panel
              type="rta"
              :id="rta.id"
              :max-files="15"
              :read-only="filesReadOnly"
              :disabled="filesReadOnly" />
          </el-collapse-item>
        </el-collapse>
      </div>

      <!-- Step 4: the Tower Co decision (RtaDecision, V5): the approved option with Decline…, the Approve-only decision, the approval e-mail and the lock line.
           A Decline moves the RTA back to Client Review or Searching: `declined` follows it to that step. -->
      <div v-else-if="viewStep === 4" class="rta-step-decision">
        <rta-decision
          ref="decision"
          :rta-data="rta"
          :read-only="readOnly || embedded"
          :lock-busy="lockRunning"
          @draft="onDraft"
          @declined="onDeclined"
          @unlock="unlockDecision" />
      </div>

      <!-- Step 5: the new site and the RTS, filled in before they exist (RtaCompletion, V6), then Complete RTA. Unlock (the lock line on Step 5)
           and Complete are this drawer's commands: they flush Step 5's auto-save first (`flushAutosaves`). -->
      <div v-else class="rta-step-completion">
        <rta-completion
          ref="completion"
          :rta-data="rta"
          :read-only="readOnly || embedded"
          :lock-busy="lockRunning"
          @unlock="unlockDecision"
          @refused="onCompletionRefused" />
      </div>

    </div>

    <!-- XOL-67 (decision 130; UX spec 2.2.6): the step footer, RequestWorkflow's `workflow-footer` (XOL-607): one flex row of three cells in DOM order =
         visual order = focus order, no positioning. The left cell is Previous Step (empty on Step 1), or Back to Searching on Step 3 while the options are in
         Client Review (it takes Previous's place: it is an act, not navigation, U-4). The middle cell is the step's own act, primary first: Propose Option(s)
         on Step 2, Submit decision on Step 4, Complete RTA on Step 5, then Reject RTA… on Steps 1 to 4 (the Tower Co closer). The right cell is Next Step.
         Stacked (xs / sm / md) it is Previous, the acts, Next, and an empty cell is hidden. The footer bar below (RTA # and Activity) stays under it. -->
    <div class="rta-stepfoot" :class="{ 'rta-stepfoot--stacked': isMediumOrLess }">

      <div class="rta-stepfoot__prev" :class="{ 'rta-stepfoot__prev--empty': !showBack && viewStep <= 1 }">

        <div v-if="showBack" class="rta-stepfoot__row rta-back-row">
          <el-button
            ref="backButton"
            type="warning"
            plain
            icon="fa fa-undo"
            :loading="backing"
            :disabled="backing || busyCommand"
            aria-describedby="rta-back-hint"
            @click="backToSearching">{{ backing ? 'Going back…' : 'Back to Searching' }}</el-button>
          <small id="rta-back-hint" class="rta-stepfoot__hint">{{ hints.backToDraft }}</small>
        </div>

        <el-button v-else-if="viewStep > 1" type="text" @click="goToStep(viewStep - 1)">&lsaquo; Previous Step</el-button>
      </div>

      <div class="rta-stepfoot__action" :class="{ 'rta-stepfoot__action--empty': !hasStepAction }">

        <div class="rta-stepfoot__pair">

          <!-- Reject RTA… (decision 142): the closer's way to end the RTA, on Steps 1 to 4. Carlos 2026-10-29: it comes FIRST in the pair, so the forward act (Propose,
               Submit decision) sits on its right, next to Next Step, in the DOM (so the focus order and the stacked order follow). It follows the STAGE, not the step being viewed (an open RTA that is
               not yet in Finalize); it is not drawn (not shown disabled) for anyone else. It asks for a reason. -->
          <!-- Decision 192 (Carlos, 2026-10-29): on a phone (stacked) the forward act comes FIRST and Reject after it, in the DOM, so the focus order follows
               what is seen; the wide footer keeps Reject first (left). One of the two Reject rows is drawn, never both (same id and ref). -->
          <div v-if="showReject && !isMediumOrLess" key="reject-first" class="rta-stepfoot__row rta-reject-row">
            <el-button
              ref="rejectButton"
              type="danger"
              plain
              icon="fa fa-ban"
              :disabled="busyCommand"
              aria-describedby="rta-reject-hint"
              @click="startReject">Reject RTA…</el-button>
            <small id="rta-reject-hint" class="rta-stepfoot__hint">{{ hints.reject }}</small>
          </div>

          <!-- Step 2, Searching: Propose Option(s) to the other side. It confirms first (decision 143); disabled with no Draft option, and the hint says why. -->
          <div v-if="showPropose" class="rta-stepfoot__row rta-propose-row">
            <el-button
              ref="proposeButton"
              type="success"
              icon="fa fa-paper-plane"
              :loading="proposing"
              :disabled="!proposeEnabled || busyCommand"
              :aria-describedby="proposeHintText ? 'rta-propose-hint' : null"
              @click="startPropose">{{ proposeLabel }}</el-button>
            <small v-if="proposeHintText" id="rta-propose-hint" class="rta-stepfoot__hint">{{ proposeHintText }}</small>
          </div>

          <!-- Step 4: Submit decision. A Tower Co user sees it (disabled unless the closer may press it); the client, a record seen from a site and a read-only
               drawer do not. -->
          <rta-decision-action
            v-if="showSubmitDecision"
            ref="stepAction"
            :action="decisionAction"
            :busy="advancing"
            :telco="telcoName"
            :changed-option-no="changedOptionNo"
            :changed-by-party="optionCounts.approvedChangedByParty"
            :kept-text="keptText"
            @click="advance" />

          <!-- Step 5: Complete RTA, filled in before it exists; the Unlock and Complete commands flush Step 5's auto-save first. -->
          <div v-if="showComplete" class="rta-stepfoot__row rta-complete-row">
            <el-button
              ref="completeButton"
              type="success"
              icon="fa fa-flag"
              :loading="completing"
              :disabled="completing || completeBusy"
              aria-describedby="rta-step5-action-hint"
              @click="completeRta">Complete RTA</el-button>
            <small id="rta-step5-action-hint" class="rta-stepfoot__hint">{{ completeHint }}</small>
          </div>

          <!-- Decision 192: on a phone Reject comes after the forward act. -->
          <div v-if="showReject && isMediumOrLess" key="reject-last" class="rta-stepfoot__row rta-reject-row">
            <el-button
              ref="rejectButton"
              type="danger"
              plain
              icon="fa fa-ban"
              :disabled="busyCommand"
              aria-describedby="rta-reject-hint"
              @click="startReject">Reject RTA…</el-button>
            <small id="rta-reject-hint" class="rta-stepfoot__hint">{{ hints.reject }}</small>
          </div>

        </div>

      </div>

      <div class="rta-stepfoot__next">
        <el-button type="text" :disabled="!canNext" @click="goToStep(viewStep + 1)">Next Step &rsaquo;</el-button>
      </div>

    </div>

    <!-- ALL the dialogs live in this one container, which is always there: each dialog moves its element to <body>, so a node that came and went among
         this section's children shifted the unkeyed ones, and a dialog open at the time made Vue insert before an element that was no longer a child here (a
         NotFoundError that left the dialog open). Inside the container the dialogs are keyed too. The Rejected reason above has an always-drawn wrapper. -->
    <div class="rta-dialogs">
      <!-- The Propose confirm (decision 143, UX spec 4.6, mock M-7g): the Draft options that go, who is e-mailed, and on the first proposal that the request then
           locks to a Tower Co manager. Nothing is sent until Propose is pressed here. -->
      <rta-dialog
        v-if="proposeOpen"
        key="propose-dialog"
        :visible="true"
        :icon="proposeCopy.icon"
        :title="proposeCopy.title"
        :subtitle="proposeCopy.subtitle"
        :ok-text="proposeCopy.okText"
        :ok-type="proposeCopy.okType"
        :loading="proposing"
        @confirm="doPropose"
        @cancel="cancelPropose">

        <p>{{ proposeCopy.lead }}</p>
        <ul class="rta-confirm-list rta-propose-items">
          <li v-for="item in proposeCopy.items" :key="item.optionNo"><b>Option {{ item.optionNo }}<template v-if="item.title">:</template></b><template v-if="item.title"> {{ item.title }}</template></li>
        </ul>
        <info-meta-list v-if="proposeCopy.meta.length" :rows="proposeMeta" :narrow="isMediumOrLess" />
      </rta-dialog>

      <!-- The confirm of Step 4's bottom button, Submit decision (UX spec 4.6): the decision locks and Step 5 opens. -->
      <rta-dialog
        v-if="stepConfirm"
        key="confirm-dialog"
        :visible="true"
        :icon="confirmCopy.icon"
        :title="confirmCopy.title"
        :subtitle="confirmCopy.subtitle"
        :ok-text="confirmCopy.okText"
        :ok-type="confirmCopy.okType"
        :loading="stepConfirmBusy"
        @confirm="doStepConfirm"
        @cancel="cancelStepConfirm">

        <p v-for="(line, i) in confirmCopy.paragraphs" :key="i">{{ line }}</p>

        <el-alert
          v-if="confirmCopy.warning"
          class="rta-confirm-warning"
          type="warning"
          show-icon
          :closable="false"
          :title="confirmCopy.warning.title"
          :description="confirmCopy.warning.description" />
      </rta-dialog>

      <!-- Reject RTA… (decision 142, E20; UX spec 2.7, mock M-26): one dialog with a required reason; confirming ends the RTA as Rejected at once. -->
      <rta-dialog
        v-if="rejectOpen"
        key="reject-dialog"
        :visible="true"
        :icon="rejectCopy.icon"
        :title="rejectCopy.title"
        :subtitle="rejectCopy.subtitle"
        :ok-text="rejectCopy.okText"
        :ok-type="rejectCopy.okType"
        :loading="rejecting"
        focus-selector=".rta-reject-field textarea"
        @confirm="doReject"
        @cancel="cancelReject">

        <p v-for="(line, i) in rejectCopy.paragraphs" :key="i">{{ line }}</p>

        <el-form label-position="top" @submit.native.prevent>
          <text-input
            class="rta-reject-field"
            type="textarea"
            required
            :label="rejectCopy.reasonLabel"
            :rows="4"
            :value="rejectReason"
            :error="rejectError || null"
            :validate-event="false"
            @input="onRejectReason" />
        </el-form>
      </rta-dialog>


      <!-- The Complete RTA confirm (UX v3 section 5): what happens, the unsent warning (decision 49) and the optional remarks. No name or ID
           fields: they are in Step 5. -->
      <rta-dialog
        v-if="completeConfirmOpen"
        key="complete-dialog"
        :visible="true"
        :icon="completeCopy.icon"
        :title="completeCopy.title"
        :subtitle="completeCopy.subtitle"
        :width="completeCopy.width"
        :ok-text="completeCopy.okText"
        :ok-type="completeCopy.okType"
        :loading="completeBusy"
        focus-selector=".rta-complete-field textarea"
        @confirm="doComplete"
        @cancel="cancelComplete">

        <p>{{ completeCopy.lead }}</p>
        <ul class="rta-confirm-list">
          <li v-for="(item, i) in completeCopy.items" :key="i">{{ item }}</li>
        </ul>

        <!-- Decision 86: a handover date of today or earlier. The nightly run, not Complete, moves the site; it comes BEFORE the unsent warning. -->
        <el-alert
          v-if="completeCopy.handoverWarning"
          class="rta-confirm-warning rta-confirm-handover"
          type="warning"
          show-icon
          :closable="false"
          :title="completeCopy.handoverWarning.title"
          :description="completeCopy.handoverWarning.description" />

        <el-alert
          v-if="completeCopy.warning"
          class="rta-confirm-warning"
          type="warning"
          show-icon
          :closable="false"
          :title="completeCopy.warning.title"
          :description="completeCopy.warning.description" />

        <el-form label-position="top" @submit.native.prevent>
          <text-input
            class="rta-complete-field"
            type="textarea"
            :label="completeCopy.fieldLabel"
            :rows="3"
            :value="completeRemarks"
            :validate-event="false"
            @input="onCompleteRemarks" />
        </el-form>
      </rta-dialog>
    </div>

    <!-- Footer (RequestWorkflow.vue:248-289). Activity and Emails Update are for the Tower Co only (decisions 18, 37) and on EVERY
         stage, Completed and Rejected included (decision 56): the lists and the log outlive the request. Decision 65: the
         read-only RTA record inside a site drawer (embedded) has neither. -->
    <el-row class="rta-footer">
      <el-col :span="8">
        <small class="text-muted">Ref <b>{{ reference }}</b></small>
      </el-col>

      <el-col :span="16" align="right">
        <div v-if="showFooterLinks" class="request-footer-links">
          <popup
            :heading="activityHeading"
            button-text="Activity"
            button-style="text"
            :show-save="false"
            allow-view-only
            button-icon="fa fa-history"
            @open="reloadActivity">

            <request-activity-log ref="activity" :row-data="activityRow" />

          </popup>

          <popup
            heading="Configure Emails"
            button-text="Emails Update"
            button-style="text"
            :show-save="false"
            :center-button="true"
            button-icon="fa fa-envelope"
            @open="reloadEmailSetup">

            <email-setup ref="emailSetup" :row-data="actionData" />

          </popup>
        </div>
      </el-col>
    </el-row>

  </section>
</template>


<script>

/*
 * XOL-67 (V3, v2.1): the RTA drawer. An RTA is the record until it closes (decision 5): the request in `state.nominal`, the options on `action_id`, no site
 * row until the Tower Co completes it. This is the shell: header, the five-step stepper, step navigation, the status panel, the mail-off banner, the step
 * footer and the commands of Steps 1 to 3 (Propose Option(s), Back to Searching, Reject RTA…). Step 1 is RtaRequest (the request as an inline form),
 * Steps 2 and 3 the options, Step 4 RtaDecision (V5) and Step 5 RtaCompletion (V6).
 *
 * Every derivation is `rtaStage` / `rtaPanel` / `rtaOption` (pure), wrapped in `computed` so a gate follows the store as it hydrates (CLAUDE.md: no `data()`
 * snapshot of org() / profile()). Nothing here tests view-only or manager status itself (decision 92): the gates are the lib's.
 */

import axios from '@/lib/axiosClient'
import RequestMixins from '@/mixins/RequestMixins'
import DrawerBusyMixin from '@/mixins/DrawerBusyMixin'

import * as sync from '@/lib/requestRowSync'
import * as rtaStage from '@/lib/rtaStage'
import * as rtaPanel from '@/lib/rtaPanel'
import * as rtaDecision from '@/lib/rtaDecision'
import * as rtaCompletion from '@/lib/rtaCompletion'
import * as rtaOption from '@/lib/rtaOption'
import { isReplay, parseFailure } from '@/lib/rtaRefusal'
import * as approvalLock from '@/lib/approvalLock'
import { normaliseOrgCategory } from '@/lib/contactRoles'
import { activityText } from '@/lib/requestActivity'
import { appNowDateSql } from '@/lib/appTime'
import { replaceDrawerArgs, revealDrawer, pushDrawer } from '@/lib/layerNav'

import RtaCommandMixins, { RTA_CHANGED, RTA_DRAWER, RTA_DRAWER_REQUEST } from '@/mixins/RtaCommandMixins'

import EmailSetup from '@/components/requests/EmailSetup'
import RequestActivityLog from '@/components/requests/RequestActivityLog'
import InfoMetaList from '@/components/requests/InfoMetaList'
import GoogleMap from '@/components/atoms/GoogleMap'
import FilesPanel from '@/components/atoms/files/FilesPanel'
import TextInput from '@/components/atoms/inputs/TextInput'
import SiteCandidates from '@/components/sites/SiteCandidates'
import ReadOnlyRemarks from '@/components/atoms/ReadOnlyRemarks'
import RtaRequest from '@/components/requests/acquire/RtaRequest'
import RtaDecision from '@/components/requests/acquire/RtaDecision'
import RtaDecisionAction from '@/components/requests/acquire/RtaDecisionAction'
import RtaCompletion from '@/components/requests/acquire/RtaCompletion'
import RtaDialog from '@/components/requests/acquire/RtaDialog'
import RtaMarkerTable from '@/components/requests/acquire/RtaMarkerTable'

// The Acquire list (RequestTable, type RTA) listens here to read its rows again; it reads its Needs My Action count after every load.
var RTA_LIST_REFRESH = 'rta-action-table-refresh-table'
var LIST_REFRESH_MS = 400

// The Options table (SiteCandidates, `tableRef` 'siteCandidatesTable') reads its rows again on this event.
var OPTIONS_REFRESH = 'siteCandidatesTable-refresh-table'

// A key of the RTA as the list could show it: the whole value, but for `state`, the decision e-mail's last batch (state.rta.tc.decisionMail),
// which no list column reads and which moves while an e-mail is going out.
function listSignature (key, value) {
  if (key === 'state' && value && value.rta && value.rta.tc && typeof value.rta.tc === 'object') {
    var tc = Object.assign({}, value.rta.tc)

    delete tc.decisionMail

    return JSON.stringify(Object.assign({}, value, { rta: Object.assign({}, value.rta, { tc: tc }) }))
  }

  return JSON.stringify(value === undefined ? null : value)
}

export default {
  name: 'RtaDetails',

  mixins: [RequestMixins, RtaCommandMixins, DrawerBusyMixin],

  // The form scope of this drawer (RequestWorkflow's pattern): Emails Update and the mail-off switch save
  // `emails.*` through the per-field save, which needs a scope to flush on close. A plain instance property
  // (it must not become reactive).
  provide () {
    this.syncScope = sync.createScope('request-drawer')

    return {
      requestSyncScope: this.syncScope
    }
  },

  components: {
    RtaMarkerTable,
    EmailSetup,
    RequestActivityLog,
    InfoMetaList,
    GoogleMap,
    FilesPanel,
    TextInput,
    SiteCandidates,
    ReadOnlyRemarks,
    RtaRequest,
    RtaDecision,
    RtaDecisionAction,
    RtaCompletion,
    RtaDialog
  },

  props: {

    // The action (GET /actions/{id}, the RTA shape).
    rowData: {
      type: Object,
      default: null
    },

    // The drawer's legacy `tab` arg: read once, and only when it names a step ('3' / 'step3').
    tab: {
      type: String,
      default: null
    },

    // The drawer's `step` arg (1 to 5); null opens the current step. (Not named `step`: RequestMixins
    // watches a `step` of its own.)
    stepArg: {
      type: Number,
      default: null
    },

    // No command buttons: the record of a closed RTA seen from elsewhere.
    readOnly: {
      type: Boolean,
      default: false
    },

    // Inside a site drawer (decision 25): no stepper, no step routing in the URL, no footer links (decision 65).
    embedded: {
      type: Boolean,
      default: false
    }

  },


  data () {
    return {
      // Decision 188: the step map's Markers table and its pins highlight each other.
      stepHoverKey: null,
      stepHighlightKey: null,
      actionData: this.rowData,
      // Set in created(): it needs the computed `maxStep`, which data() cannot read.
      viewStep: 1,
      panelId: 'rta-status-title-' + this._uid,

      // The action's contentVersion, as the drawer last read it. adoptAction does not copy the versions into the action (they belong to the per-field save of
      // Emails Update), so Step 1, which has a version of its own to send, reads this one.
      rowVersion: this.rowData && typeof this.rowData.contentVersion === 'number' ? this.rowData.contentVersion : null,

      // Steps 2 and 3: the Map / Files collapses (the names open). The Map starts open on Step 2 (decision 131, `panelsFor`), closed on Step 3.
      openPanels: [],

      // The option rows the map draws its pins from (Step 1's and Steps 2 and 3's): read here, and again whenever the options table reads.
      optionRows: [],

      // Step 4: what the decision form shows ({ status, remarks }), so the bottom button follows it; null off Step 4.
      decisionDraft: null,

      // Step 4's Submit decision: a flush or a command is in flight; the confirm is open ('finalize' | null), its command is running, and whether the decision
      // was never e-mailed (decision 49).
      advancing: false,
      stepConfirm: null,
      stepConfirmBusy: false,
      confirmUnsent: false,

      // Step 5's Complete RTA: the flush and precheck before the confirm are running; the confirm is open (with what it names, the
      // decision 49 flag and the optional remarks); its command is running.
      completing: false,
      completeConfirmOpen: false,
      completeBusy: false,
      completeSnap: null,
      completeUnsent: false,
      completeRemarks: '',

      // Step 2's Propose Option(s): the flush and the read of the options before the confirm (or the command) are running; the confirm is open.
      proposing: false,
      proposeOpen: false,

      // Step 3's Back to Searching: its confirm and its command are running.
      backing: false,

      // Reject RTA…: the dialog is open, its command is running, the reason typed and the message under it.
      rejectOpen: false,
      rejecting: false,
      rejectReason: '',
      rejectError: ''
    }
  },


  computed: {

    // The store, as computed (not data()): it hydrates after the first paint. `org` / `profile` are the
    // AccessMixins methods, hence the other names.
    viewerOrg () {
      return this.org()
    },

    viewerProfile () {
      return this.profile()
    },

    // The viewer's name as the API stamps it (`lastReset.by`: the contact's name, else the login). A computed: the profile hydrates after the first paint.
    viewerName () {
      var p = this.viewerProfile

      // trimmed, with the login when the name is blank: the API's ActorStamp::name
      return p ? (String(p.name || '').trim() || String(p.login || '').trim() || null) : null
    },

    // The option a saved reset names (state.rta.lastReset.optionId), or null: the notice looks its current number up in the option rows.
    resetOptionId () {
      var s = this.rta && this.rta.state && this.rta.state.rta ? this.rta.state.rta : null
      var reset = s && s.lastReset && typeof s.lastReset === 'object' ? s.lastReset : null

      return reset && reset.optionId ? reset.optionId : null
    },

    rta () {
      return this.actionData
    },

    // The RTA's name (the request's Site Name, until Complete: then the site's) and its RTA-… reference (decisions 138, 158).
    title () {
      return rtaStage.rtaTitle(this.rta)
    },

    reference () {
      return rtaStage.referenceOf(this.rta)
    },

    // The client: GET /actions/{id} appends it (id, name, shortName, category, colourCode).
    client () {
      return (this.rta && (this.rta.client || this.rta.organisation)) || null
    },

    telcoName () {
      var c = this.client

      return c ? (c.name || c.shortName || '') : ''
    },

    clientBadge () {
      var c = this.client

      return !!(c && c.colourCode && c.shortName)
    },

    towerCo () {
      return this.towerCoName()
    },

    // Is the client a Tower Co itself (then there is no RTS, decision 4)?
    tcClient () {
      var c = this.client

      return !!(c && normaliseOrgCategory(c.category) === 'Tower Company')
    },

    // Who is looking: the client, a Tower Co closer (project / finance / admin manager), or any other Tower Co
    // user (view-only included). The panel's copy is keyed on it.
    role () {
      if (!this.isTowerCo()) {
        return 'client'
      }

      return rtaStage.isTcCloser(this.rta, this.viewerOrg, this.viewerProfile) ? 'tcc' : 'tce'
    },

    // Which side made the request, for the no-options panel: the API's `createdByParty` ('owner' | 'towerco'), on the full action and on the compact `rta`
    // (state.rta of a full one). null, missing and anything else is '' = unknown: the panel then says it neutrally. The creating contact's id
    // (`state.rta.createdBy`) is NOT read for a side: it says who, not which organisation.
    createdBy () {
      var s = this.rta && this.rta.state && this.rta.state.rta ? this.rta.state.rta : {}
      var party = this.rta && this.rta.createdByParty !== undefined ? this.rta.createdByParty : s.createdByParty

      return party === 'towerco' || party === 'owner' ? party : ''
    },

    copyCtx () {
      return {
        role: this.role,
        telco: this.telcoName,
        towerCo: this.towerCo,
        siteName: this.title,
        tcClient: this.tcClient,
        embedded: this.embedded,
        org: this.viewerOrg,
        // The reset notice (rtaPanel.resetNotice): the viewer's name tells "You declined" from another manager's decline, and the option rows give the
        // option's CURRENT number (state.rta.lastReset names it by id; numbers shift when an option is deleted, P-S20).
        userName: this.viewerName,
        options: this.optionRows,
        createdBy: this.createdBy,
        fmtDate: this.formatDate,
        fmtDateTime: this.formatDateTime,
        // The Completed panel says "at the next nightly run" for a handover date of today or earlier (decision 86). Read once, when the copy is built.
        today: appNowDateSql()
      }
    },

    stage () {
      return rtaStage.stageMeta(this.rta.status)
    },

    // The five steps' names and icons (a computed, so Vue does not make the shared array reactive).
    stepList () {
      return rtaStage.STEPS
    },

    steps () {
      return rtaStage.stepState(this.rta)
    },

    // el-steps :active. A Completed RTA is all five finished.
    activeIndex () {
      return this.steps.terminal === 'completed' ? 5 : this.steps.current - 1
    },

    stepStatuses () {
      return this.steps.statuses
    },

    suffix () {
      return rtaPanel.stageSuffix(this.rta)
    },

    cueLine () {
      return rtaPanel.cue(this.rta, this.copyCtx)
    },

    panel () {
      return rtaPanel.statusPanel(this.rta, this.copyCtx)
    },

    resetText () {
      return rtaPanel.resetNotice(this.rta, this.copyCtx)
    },

    // The reason a Rejected RTA was ended with (E20 `remarks`; the action carries it as `closeReason`), or '' (any other stage, or none given).
    rejectedReason () {
      return this.rta.status === 'Rejected' && this.rta.closeReason ? String(this.rta.closeReason) : ''
    },

    siteGone () {
      return !!rtaStage.siteDeleted(this.rta)
    },

    // "Changed since submitted" is the Tower Co's cue (never the client's) on a stage that waits on the client's submit.
    showChanged () {
      return rtaStage.showsChangedChip(this.rta, this.role !== 'client')
    },

    // The furthest step that can be opened: the current one (Step 5 only in Finalize or Completed, Step 4 only once an option is approved). Step 1 is always open.
    // (decision 162: Step 4 stays openable in Client Approved after the cursor went back to Step 3 for an unsubmitted change of the approved option.)
    maxStep () {
      return rtaStage.furthestStep(this.rta)
    },

    canNext () {
      return this.viewStep < this.maxStep
    },

    hints () {
      return rtaPanel.HINTS
    },

    /* ---- The footer's acts ---- */

    // A command is running: the other footer buttons wait (UX spec 5), and the drawer does not close under it (LayerHost's busy veto).
    busyCommand () {
      return !!this.lockRunning || this.advancing || this.stepConfirmBusy || this.completing || this.completeBusy || this.proposing || this.backing || this.rejecting
    },

    // Reject RTA… (decision 142): the closer, an open RTA before Finalize, on Steps 1 to 4. The STAGE decides (canRejectRta), not the step being viewed;
    // not in a site's record or a read-only drawer.
    showReject () {
      return !this.embedded && !this.readOnly && this.viewStep <= 4 && rtaStage.canRejectRta(this.rta, this.viewerOrg, this.viewerProfile)
    },

    // Step 3 while the options are in Client Review: Back to Searching (the client or a Tower Co responder) takes Previous Step's place.
    showBack () {
      return !this.embedded && !this.readOnly && this.viewStep === 3 && rtaStage.canBackToDraft(this.rta, this.viewerOrg, this.viewerProfile)
    },

    // Step 2 while the RTA is in Searching: Propose Option(s), for the client or a Tower Co responder (drawn disabled, with the reason, when there is nothing to
    // propose). Not drawn for a view-only user.
    showPropose () {
      return !this.embedded && !this.readOnly && this.viewStep === 2 && this.rta.status === 'Searching' &&
        rtaStage.canRespond(this.rta, this.viewerOrg, this.viewerProfile)
    },

    optionCounts () {
      return rtaStage.optionSummary(this.rta)
    },

    proposeEnabled () {
      return rtaStage.canPropose(this.rta, this.viewerOrg, this.viewerProfile, this.optionCounts.draftCount)
    },

    // Who a Propose goes to: 'client' (the Tower Co proposes to the Telco), 'towerco' (the client proposes), 'self' (a Tower Co client: no side named).
    proposeTarget () {
      return rtaStage.proposeTarget(this.rta, this.viewerOrg)
    },

    proposeLabel () {
      if (this.proposing) {
        return 'Proposing…'
      }

      return this.proposeTarget === 'self'
        ? 'Propose Option(s)'
        : 'Propose Option(s) to ' + (this.proposeTarget === 'towerco' ? this.towerCo : this.telcoName)
    },

    // The hint under the button: who is e-mailed, or why it is disabled (UX spec 4.4).
    proposeHintText () {
      var none = this.optionCounts.optionCount === 0

      return rtaOption.proposeHint(this.proposeTarget, {
        noOptions: none,
        noDraft: !none && this.optionCounts.draftCount === 0,
        mailOff: !this.mailOn,
        mailPossible: this.mailOn && this.proposeTarget !== 'self',
        telco: this.telcoName,
        towerCo: this.towerCo
      })
    },

    // The Draft options a Propose would send (the confirm lists them).
    draftOptions () {
      return this.optionRows.filter(function (o) {
        return !!o && o.optionStatus === 'Draft'
      }).map(function (o) {
        return { optionNo: o.optionNo, title: o.title || '' }
      })
    },

    // The words of the Propose confirm (rtaDecision.proposeConfirm): the options that go, who is e-mailed, and, on the first proposal, that the request then locks.
    proposeCopy () {
      return rtaDecision.proposeConfirm({
        name: this.title,
        reference: this.reference,
        target: this.proposeTarget,
        options: this.draftOptions,
        // The E-mailed row: a Tower Co client (no side) and Send E-mails off win inside proposeConfirm; otherwise an e-mail goes.
        mailPossible: true,
        mailOff: !this.mailOn,
        firstProposal: !rtaStage.hasBeenProposed(this.rta),
        telco: this.telcoName,
        towerCo: this.towerCo
      })
    },

    proposeMeta () {
      return this.proposeCopy.meta.map(function (row) {
        return { key: row.label, label: row.label, value: row.value }
      })
    },

    // The words of the Reject RTA… dialog (rtaDecision.rejectRtaConfirm).
    rejectCopy () {
      return rtaDecision.rejectRtaConfirm({
        name: this.title,
        reference: this.reference,
        telco: this.telcoName,
        towerCo: this.towerCo,
        tcClient: this.tcClient,
        mailPossible: !this.tcClient && this.mailOn,
        mailOff: !this.tcClient && !this.mailOn
      })
    },

    // The step footer's middle cell has something in it (else the cell is empty, and hidden when stacked).
    hasStepAction () {
      return this.showPropose || this.showSubmitDecision || this.showComplete || this.showReject
    },

    /* ---- Step 4's bottom button, Submit decision (the gate is the lib's) ---- */

    // The RTA as the decision form on screen would make it. The button follows what the user sees decided; pressing it flushes the
    // auto-save first and judges again on what the server then holds (advance).
    step4Rta () {
      return rtaDecision.withDraft(this.rta, this.decisionDraft)
    },

    decisionAction () {
      return rtaStage.step3Action(this.step4Rta, this.viewerOrg, this.viewerProfile)
    },

    // A Tower Co user sees it (disabled unless they are the closer); not the client, a site's record, or a read-only drawer.
    showSubmitDecision () {
      return !this.embedded && !this.readOnly && this.viewStep === 4 && this.decisionAction.visible
    },

    // The number of the approved option (the hint names it).
    changedOptionNo () {
      return this.optionCounts.approvedOptionNo
    },

    // After an Unlock from Step 5: "Step 5 entries saved on {date} by {name} are kept …".
    keptText () {
      return this.decisionAction.mode === 'finalize' ? rtaDecision.keptLine(rtaDecision.keptEntries(this.rta), { fmtDate: this.formatDate }) : ''
    },

    // The words of the Submit decision confirm (UX spec 4.6).
    confirmCopy () {
      return rtaDecision.finalizeConfirm({
        name: this.title,
        reference: this.reference,
        telco: this.telcoName,
        towerCo: this.towerCo,
        tcClient: this.tcClient,
        unsent: this.confirmUnsent
      })
    },

    /* ---- Step 5's Complete RTA (B6, decisions 40, 45) ---- */

    // The TC closer, in Finalize, on Step 5; not in a site's record or a read-only drawer.
    showComplete () {
      return !this.embedded && !this.readOnly && this.viewStep === 5 && rtaStage.canComplete(this.rta, this.viewerOrg, this.viewerProfile)
    },

    completeHint () {
      return rtaCompletion.completeHint()
    },

    // The words of the Complete confirm (UX v3 section 5).
    completeCopy () {
      var snap = this.completeSnap || {}

      return rtaCompletion.completeConfirm({
        siteName: snap.siteName,
        siteId: snap.siteId,
        optionNo: snap.optionNo,
        targetDate: snap.targetDate,
        rtsOutcome: snap.rtsOutcome,
        rtsLink: snap.rtsLink,
        others: snap.others,
        handoverDate: snap.handover ? this.formatDate(snap.handover) : '',
        handoverStatus: snap.handoverStatus,
        handoverPast: !!snap.handoverPast,
        kept: !!snap.kept,
        transitionedStatus: snap.transitionedStatus,
        telco: this.telcoName,
        towerCo: this.towerCo,
        tcClient: this.tcClient,
        mailPossible: !this.tcClient && rtaStage.mailOn(this.rta),
        unsent: this.completeUnsent
      })
    },

    // A command is running: the drawer does not close under it (LayerHost's busy veto).
    drawerBusy () {
      return this.busyCommand
    },

    /* ---- Steps 2 and 3: the options table and the Map ---- */

    // The request in the shape of a site, for the Options table and its opener.
    optionSite () {
      return rtaOption.requestSite(rtaStage.rtaOf(this.rta)) || { id: null, siteName: this.title, siteId: this.reference, count: 0 }
    },

    // The pins of the step Map: the request's crosshairs, every option's pin and extra markers, all read-only (decisions 144, 147, 156).
    stepPins () {
      return rtaOption.markersForStepMap(this.rta, this.optionRows)
    },

    // Decision 188: one row per pin of the step map; an extra marker names its option ("Access 1 · option 2"); nothing is deleted here.
    stepMarkerRows () {
      return rtaOption.markerTableRows(this.stepPins, { withOption: true })
    },

    // Where the Map starts: the request's point, else the middle of the pins; null when nothing has a point ("No option has a point yet.").
    stepMapCentre () {
      var at = rtaOption.requestPoint(this.rta)

      if (at) {
        return { lat: at.lat, lng: at.lng, zoom: 16 }
      }

      var c = rtaOption.markersCentre(this.stepPins)

      return c ? { lat: c.lat, lng: c.lng, zoom: 14 } : null
    },

    // A Google map made inside a closed (display: none) panel has no size, so it is made again when its panel opens.
    mapKey () {
      return 'map-' + this.viewStep + '-' + (this.openPanels.indexOf('map') !== -1 ? 'open' : 'closed')
    },

    // DEV-25: the RTA's files are written by a Tower Co user who is not view-only, on an open RTA; everyone else reads.
    filesReadOnly () {
      return this.embedded || this.readOnly || !this.isTowerCo() || !!this.isViewOnly() || rtaStage.isTerminal(this.rta)
    },

    currentStepMeta () {
      return rtaStage.STEPS[this.viewStep - 1] || rtaStage.STEPS[0]
    },

    isOpen () {
      return !rtaStage.isTerminal(this.rta)
    },

    // The banner shows to every viewer of an OPEN RTA whose own Send E-mails switch is off.
    mailBanner () {
      return this.isOpen && this.requestMailOff()
    },

    mailOn () {
      return !this.requestMailOff()
    },

    canToggleMail () {
      return !!(this.actionData.emails && this.isTowerCo() && this.isManager())
    },

    // Activity and Emails Update: the Tower Co only, on every stage; not in a site drawer's record.
    showFooterLinks () {
      return this.isTowerCo() && !this.embedded
    },

    activityHeading () {
      return activityText('requestActivity.heading', { rtx: 'RTA', id: this.rta.id })
    },

    // The Activity log reads the request's `steps` for its Step filter and `organisation` for the Telco's name. An RTA
    // has neither (its steps are fixed), so the log is given a view of it that does.
    activityRow () {
      return Object.assign({}, this.rta, {
        actionType: 'RTA',
        steps: rtaStage.activitySteps(),
        organisation: this.rta.organisation || this.rta.client || null
      })
    },

    // The site and the RTS a Completed RTA made: Tower Co callers get `completion`, the client `{siteId, rtsId}`.
    siteLinkId () {
      var c = this.rta.completion

      return (c && c.siteId) || this.rta.site_id || null
    },

    rtsLinkId () {
      var c = this.rta.completion

      return (c && c.rtsId) || null
    }

  },


  watch: {

    rowData (value) {
      this.actionData = value
    },

    // The URL moved the step (Back / Forward): follow it when it is a step that can be opened.
    stepArg (value) {
      if (this.openable(value)) {
        this.viewStep = value
      }
    },

    // The RTA moved on under us (a re-read): never stay on a step it can no longer show.
    maxStep (value) {
      if (this.viewStep > value) {
        this.viewStep = value
      }
    },

    // A reset names an option by id: read the rows so the notice shows its current number. Only on Steps 4 and 5: Step 1 reads them for its map, and Steps 2 and
    // 3 have the options table, which reads them (and tells the drawer, `loaded`).
    resetOptionId (value) {
      if (value && this.viewStep >= 4) {
        this.loadOptionRows()
      }
    },

    viewStep (value) {
      this.openPanels = this.panelsFor(value)

      // Step 1's map draws the options' pins: read them again when it is opened (an add or a delete on Step 2 happened since).
      if (value === 1) {
        this.loadOptionRows()
      }
    }

  },


  methods: {

    // Can this step be opened? 1 to the current step.
    openable (n) {
      return typeof n === 'number' && n >= 1 && n <= this.maxStep && n === Math.floor(n)
    },

    // The step the drawer opens on: the `step` arg (or a legacy `tab` that names one) if it can be
    // opened, else the current step. A `step=5` link on an RTA that is not in Finalize or Completed opens the current step instead.
    initialStep () {
      var asked = this.stepArg
      var m = /^(?:step)?([1-5])$/.exec(this.tab || '')

      if (asked === null && m) {
        asked = Number(m[1])
      }

      return this.openable(asked) ? asked : this.steps.current
    },

    // The collapses of Steps 2 and 3 that start open (decision 131, Carlos: "Always have map open for step 2"): the Map on Step 2.
    // Step 3 and Files start closed. The collapse is shared by both steps, so it is set again whenever the step changes.
    panelsFor (step) {
      return step === 2 ? ['map'] : []
    },

    // The auto-saving form of a step, when it is the one on screen: Step 1's request, Step 4's decision, Step 5's entries.
    stepForm (n) {
      var form = n === 1 ? this.$refs.request : (n === 4 ? this.$refs.decision : (n === 5 ? this.$refs.completion : null))

      return form && typeof form.flush === 'function' ? form : null
    },

    // Previous / Next Step. In the site drawer's record (embedded) the URL is not the drawer's to change.
    goToStep (n) {
      var self = this

      if (!this.openable(n)) {
        return null
      }

      // Leaving Step 1, 4 or 5 with an edit waiting saves it first (a refusal keeps the user here: its notice says why).
      var form = n !== this.viewStep ? this.stepForm(this.viewStep) : null

      if (form && form.hasPending()) {
        return form.flush().then(function (r) {
          if (r.ok && !self._isDestroyed) {
            self.moveToStep(n)
          }
        })
      }

      this.moveToStep(n)

      return null
    },

    // Go to step `n` as soon as the RTA can show it: now when it can, else when the next reading of the RTA allows it (adoptAction). The user going anywhere
    // else first cancels it (moveToStep).
    followStep (n) {
      this.pendingStep = null

      if (this.openable(n)) {
        this.goToStep(n)
        this.$nextTick(this.focusStepHeading)
      } else {
        this.pendingStep = n
      }
    },

    moveToStep (n) {
      this.pendingStep = null
      this.viewStep = n

      if (!this.embedded && this.$router) {
        replaceDrawerArgs(this.$router, 'request', { step: n })
      }

      this.keepFocus()
    },

    // What the decision form on screen says ({ status, remarks }), or null when Step 4 closes.
    onDraft (draft) {
      this.decisionDraft = draft
    },

    // Step 4's Decline… went through: the RTA is back in Client Review (Step 3) or Searching (Step 2), and the drawer goes there and focuses its title. The RTA was
    // adopted first (the form announced it), so that step can be opened now; with an answer that had no `action` the move waits for the next reading (followStep).
    onDeclined (event) {
      this.followStep(event && event.stage === 'Searching' ? 2 : 3)
      this.reloadOptions()
    },

    // A pin of the step Map (or of Step 1's) was clicked: an option's pin opens that option, as its row's chevron does. The crosshairs open nothing.
    // Decision 188: a row raises its pin; a hovered pin tints its row.
    onStepRowEnter (row) {
      this.stepHighlightKey = row ? row.key : null
      this.stepHoverKey = row ? row.key : null
    },

    onStepRowLeave () {
      this.stepHighlightKey = null
      this.stepHoverKey = null
    },

    onStepPinHover (m) {
      this.stepHoverKey = m ? m.key : null
    },

    onPinClick (marker) {
      this.openOption(marker ? marker.opensOption : null)
    },

    openOption (id) {
      if (this.$router && id) {
        pushDrawer(this.$router, { open: { candidate: { id: Number(id) } } })
      }
    },


    /* ---------------------------------------------------------------------
       The commands of Steps 1 to 3: flush, then Propose Option(s) / Back to Searching / Reject RTA…
    --------------------------------------------------------------------- */

    // The auto-saving forms of this drawer that are on screen: Step 1's request, Step 4's decision, or Step 5's site and RTS entries (only one of the
    // three is ever mounted).
    autosaveForms () {
      return [this.$refs.request, this.$refs.decision, this.$refs.completion].filter(function (f) {
        return !!f && typeof f.flush === 'function'
      })
    },

    // Save what the forms of this drawer hold. Resolves true when every save was taken; false when one was refused (its notice is up).
    flushAutosaves () {
      var forms = this.autosaveForms()

      if (!forms.length) {
        return Promise.resolve(true)
      }

      return Promise.all(forms.map(function (f) { return f.flush() })).then(function (results) {
        return results.every(function (r) { return !!r.ok })
      })
    },

    // Did the last save of a form on screen get refused (and nothing newer waits)? Its notice / inline error is up and the screen holds
    // values the server does not: a command that reads the server must not go on (XOL-67 B10). Resolved BEFORE a confirm opens, so the
    // user is not asked to confirm something that will not be sent.
    savesRefused () {
      return this.autosaveForms().some(function (f) {
        return typeof f.isRefused === 'function' && f.isRefused()
      })
    },

    hasPendingSaves () {
      return this.autosaveForms().some(function (f) {
        return typeof f.hasPending === 'function' && f.hasPending()
      })
    },

    // The words a refusal needs: who is looking, and the option whose response changed.
    refusalContext (command) {
      return {
        telco: this.telcoName,
        towerCo: this.towerCo,
        role: this.role,
        changedOptionNo: this.changedOptionNo,
        changedByParty: rtaStage.approvedChangeParty(this.rta),
        command: command
      }
    },

    // A refused command: the toast in the words of its code, then the RTA is read again so the screen shows what the server holds.
    refuseCommand (result, command) {
      var failure = result && result.failure ? result.failure : {}
      var notice = failure.code === 'reason_required' ? null : this.rtaRefuse(result, this.refusalContext(command))

      // reason_required has no toast: Reject RTA… says it under its reason field.
      if (failure.status === 409 || failure.code === 'reason_required' || (notice && notice.reread)) {
        this.fetchData('/actions/' + this.rta.id, this.adoptAction)
        this.reloadOptions()
      }

      // not_transitioned: someone unlocked the approval, so the RTA is back at Step 4 and a Step 5 command was refused ("Finalize
      // entries saved earlier are kept"). The toast said so; the drawer goes to Step 4 with the RTA it holds then.
      if (failure.code === 'not_transitioned' && this.viewStep > 4) {
        this.viewStep = 4

        if (!this.embedded && this.$router) {
          replaceDrawerArgs(this.$router, 'request', { step: 4 })
        }
      }

      return notice
    },

    // The command's answer is the RTA as it is now: announced like any RTA command's (this drawer, the option layers and the list
    // follow it; onRtaChanged adopts it here). A command moves the options too (Propose puts them in review, Back to Searching and Reject RTA… change
    // their statuses), so the options table and the maps' rows are read again.
    takeAnswer (data) {
      this.rtaAnnounce(data, this.rta.id)
      this.reloadOptions()
    },

    // The options table (when it is on screen: Steps 2 and 3) and the rows the maps draw.
    reloadOptions () {
      this.$events.emit(OPTIONS_REFRESH)

      return this.loadOptionRows()
    },

    // The button that opened a dialog or a confirm gets focus back (a loading button is disabled, so it is blurred while the command runs).
    focusButton (ref) {
      var self = this

      this.$nextTick(function () {
        var b = self.$refs[ref]

        if (b && b.$el && typeof b.$el.focus === 'function') {
          b.$el.focus()
        } else {
          self.focusStepHeading()
        }
      })
    },

    /**
     Propose Option(s) (API-3, DEV-69; decision 143 confirms first):
       1. flush the forms on screen, and read the options (the confirm lists the Draft ones as they are now);
       2. judge again on what the RTA holds: if the button is no longer enabled, stop;
       3. open the confirm. Nothing is sent until Propose is pressed in it.
     */
    startPropose () {
      var self = this

      if (this.busyCommand || this.proposeOpen || !this.showPropose || !this.proposeEnabled) {
        return Promise.resolve(null)
      }

      this.proposing = true

      return this.flushAutosaves().then(function (ok) {
        return ok ? self.loadOptionRows() : null
      }).then(function (rows) {
        self.proposing = false

        if (rows === null || self._isDestroyed || !rtaStage.canPropose(self.rta, self.viewerOrg, self.viewerProfile, rtaStage.optionSummary(self.rta).draftCount)) {
          return null
        }

        self.proposeOpen = true

        return true
      })
    },

    cancelPropose () {
      if (this.proposing) {
        return
      }

      this.proposeOpen = false
      this.focusButton('proposeButton')
    },

    // POST /actions/{id}/rta/propose (API-3): the options go to the other side and the RTA is in Client Review. A replay (the same Propose a second time
    // after it went through: 200, `duplicate`) is a success only when the RTA IS in Client Review now (P-S19, rtaRefusal.isReplay).
    doPropose () {
      var self = this

      if (this.proposing || !this.proposeOpen) {
        return Promise.resolve(null)
      }

      var target = this.proposeTarget
      var mailOff = !this.mailOn

      this.proposing = true

      return this.rtaCommand('post', '/actions/' + this.rta.id + '/rta/propose', {}, 'RTA').then(function (result) {
        self.proposing = false
        self.proposeOpen = false

        if (!result.ok) {
          self.refuseCommand(result, 'propose')
          self.focusButton('proposeButton')

          return result
        }

        var data = result.data
        var stage = data && data.action ? data.action.status : null

        // A "duplicate" that did not leave the RTA in Client Review is no replay of this command: the other side proposed first.
        if (data && data.duplicate === true && !isReplay(data, 'propose', stage)) {
          var err = { response: { status: 409, data: { code: 'wrong_phase', stage: stage } } }

          self.refuseCommand({ ok: false, err: err, failure: parseFailure(err) }, 'propose')
          self.focusButton('proposeButton')

          return result
        }

        self.takeAnswer(data)
        self.rtaToast(
          rtaOption.sentence('propose', { telco: self.telcoName, towerCo: self.towerCo }, { target: target, mailOff: mailOff }),
          data,
          { actor: target === 'towerco' ? 'client' : 'towerco', orgs: [target === 'towerco' ? self.towerCo : self.telcoName], towerCo: self.towerCo }
        )

        // Client Review is Step 3: the drawer follows, and focuses its title. Only once the RTA SHOWS that stage: an answer with no `action` is read again
        // first (takeAnswer), and the move waits for it (adoptAction), so the drawer never stays on Step 2 by silence or goes where the RTA is not.
        self.followStep(3)

        return result
      })
    },

    // Back to Searching (API-4, DEV-70): a confirm (it discards the reviews that were not submitted), then POST /actions/{id}/rta/back-to-draft.
    backToSearching () {
      var self = this

      if (this.busyCommand || !this.showBack) {
        return Promise.resolve(null)
      }

      var copy = rtaOption.confirmCopy('backToDraft', { towerCo: this.towerCo })

      return this.$confirm(copy.message, copy.title, {
        confirmButtonText: copy.confirmButtonText,
        cancelButtonText: copy.cancelButtonText,
        type: copy.type,
        center: true
      }).then(function () {
        return self.doBackToSearching()
      }, function () {
        self.focusButton('backButton')

        return null
      })
    },

    doBackToSearching () {
      var self = this

      this.backing = true

      return this.flushAutosaves().then(function (ok) {
        if (!ok || self._isDestroyed) {
          self.backing = false

          return null
        }

        return self.rtaCommand('post', '/actions/' + self.rta.id + '/rta/back-to-draft', {}, 'RTA').then(function (result) {
          self.backing = false

          if (!result.ok) {
            self.refuseCommand(result, 'backToDraft')
            self.focusButton('backButton')

            return result
          }

          var data = result.data
          var stage = data && data.action ? data.action.status : null

          // A replay (200 `duplicate`, the RTA already in Searching) is the same answer (P-S19). A "duplicate" that left it elsewhere is no replay of this
          // command: the real refusal, and the RTA is read again.
          if (data && data.duplicate === true && !isReplay(data, 'backToDraft', stage)) {
            var err = { response: { status: 409, data: { code: 'wrong_phase', stage: stage } } }

            self.refuseCommand({ ok: false, err: err, failure: parseFailure(err) }, 'backToDraft')
            self.focusButton('backButton')

            return result
          }

          self.takeAnswer(data)
          self.rtaToast(rtaOption.sentence('backToDraft'), data, { actor: 'towerco', orgs: [], towerCo: self.towerCo })

          // Searching is Step 2: the drawer follows, and focuses its title.
          self.goToStep(2)
          self.$nextTick(function () { self.focusStepHeading() })

          return result
        })
      })
    },

    // Reject RTA… (API-11, E20): a dialog with a required reason, then POST /actions/{id}/rta/reject { remarks }.
    startReject () {
      var self = this

      if (this.busyCommand || this.rejectOpen || !this.showReject) {
        return Promise.resolve(null)
      }

      return this.flushAutosaves().then(function (ok) {
        if (!ok || self._isDestroyed || !self.showReject) {
          return null
        }

        self.rejectReason = ''
        self.rejectError = ''
        self.rejectOpen = true

        return true
      })
    },

    cancelReject () {
      if (this.rejecting) {
        return
      }

      this.rejectOpen = false
      this.focusButton('rejectButton')
    },

    onRejectReason (value) {
      this.rejectReason = value

      if (String(value || '').trim() !== '') {
        this.rejectError = ''
      }
    },

    doReject () {
      var self = this
      var reason = String(this.rejectReason || '').trim()

      if (this.rejecting || !this.rejectOpen) {
        return Promise.resolve(null)
      }

      // The reason is required: said under the field, nothing sent, focus back in it.
      if (!reason) {
        this.rejectError = this.rejectCopy.reasonRequired
        this.$nextTick(function () {
          var field = typeof document !== 'undefined' ? document.querySelector('.rta-reject-field textarea') : null

          if (field && typeof field.focus === 'function') {
            field.focus()
          }
        })

        return Promise.resolve(null)
      }

      this.rejecting = true

      return this.rtaCommand('post', '/actions/' + this.rta.id + '/rta/reject', { remarks: reason }, 'RTA').then(function (result) {
        self.rejecting = false

        if (!result.ok) {
          // The server wants a reason too (a blank one slipped through): it is said where the user types it.
          if (result.failure && result.failure.code === 'reason_required') {
            self.rejectError = self.rejectCopy.reasonRequired

            return result
          }

          self.rejectOpen = false
          self.refuseCommand(result, 'reject')
          self.focusButton('rejectButton')

          return result
        }

        self.rejectOpen = false
        self.takeAnswer(result.data)
        self.rtaToast(rtaDecision.sentence('reject'), result.data, { actor: 'towerco', orgs: [self.telcoName], towerCo: self.towerCo })

        // The button is gone with the RTA closed: focus goes to the Rejected panel.
        self.$nextTick(function () { self.focusPanel() })

        return result
      })
    },


    /* ---------------------------------------------------------------------
       Step 4's Submit decision and Unlock: flush, then the command
    --------------------------------------------------------------------- */

    /**
     The bottom button (decision 45): Submit decision.
       1. flush the decision form (the XOL-582 ordering rule): the stored decision is what the server judges;
       2. judge again on it: if the button is no longer enabled (the flush, or a client change, moved the decision), stop;
       3. open the confirm. It warns when the decision was never e-mailed (decision 49).
     */
    advance () {
      var self = this

      if (this.advancing || this.stepConfirm || this.stepConfirmBusy || !this.showSubmitDecision || !this.decisionAction.enabled) {
        return Promise.resolve(null)
      }

      this.advancing = true

      return this.flushAutosaves().then(function (ok) {
        self.advancing = false

        if (!ok || self._isDestroyed) {
          return null
        }

        var action = rtaStage.step3Action(self.rta, self.viewerOrg, self.viewerProfile)

        if (!action.enabled) {
          return null
        }

        self.confirmUnsent = rtaStage.decisionUnsent(self.rta, self.viewerOrg)
        self.stepConfirm = 'finalize'

        return self.stepConfirm
      })
    },

    cancelStepConfirm () {
      if (this.stepConfirmBusy) {
        return
      }

      this.stepConfirm = null
      this.focusStepAction()
    },

    // A confirm that was cancelled (or refused) gives focus back to the button that opened it. The dialog cannot: the button was
    // loading (so disabled, so blurred) while the flush ran, and the dialog opened after it.
    focusStepAction () {
      var self = this

      this.$nextTick(function () {
        var a = self.$refs.stepAction

        if (a && typeof a.focus === 'function') {
          a.focus()
        } else {
          self.focusStepHeading()
        }
      })
    },

    // The confirm's action. The form can have been edited while the confirm was open (the dialog is not modal to the form behind it
    // in the user's mind, and a pause may end meanwhile): flush once more, and judge again on what the server then holds. If the
    // flush was refused, or the decision no longer allows this command, the confirm closes with nothing sent and the button says why.
    doStepConfirm () {
      var self = this

      if (this.stepConfirmBusy || !this.stepConfirm) {
        return Promise.resolve(null)
      }

      this.stepConfirmBusy = true

      return this.flushAutosaves().then(function (ok) {
        var action = rtaStage.step3Action(self.rta, self.viewerOrg, self.viewerProfile)

        if (!ok || self._isDestroyed || !action.enabled) {
          self.stepConfirmBusy = false
          self.stepConfirm = null
          self.focusStepAction()

          return null
        }

        return self.doFinalize()
      })
    },

    // POST /actions/{id}/rta/transition (E17): locks the decision and opens Step 5. No mail, no notice.
    doFinalize () {
      var self = this
      var tc = rtaStage.tcDecision(this.rta)

      this.stepConfirmBusy = true

      return this.rtaCommand('post', '/actions/' + this.rta.id + '/rta/transition', { expectedApprovedStatus: 'Approved', expectedOptionId: tc.optionId }, 'RTA').then(function (result) {
        self.stepConfirmBusy = false
        self.stepConfirm = null

        if (!result.ok) {
          self.refuseCommand(result, 'finalize')
          self.focusStepAction()
          return result
        }

        self.takeAnswer(result.data)
        self.$message({ message: rtaDecision.sentence('finalize'), type: 'success' })

        // Step 5 is now reachable (the stage is Finalize): go there, and focus its title.
        self.goToStep(5)
        self.$nextTick(function () { self.focusStepHeading() })

        return result
      })
    },

    // The lock line's Unlock: a confirm first (it removes protection), then flush and POST /actions/{id}/rta/decision/unlock (E16).
    // From Finalize the RTA returns to Step 4 and keeps the Step 5 entries (decision 46).
    unlockDecision () {
      var self = this

      if (this.lockRunning || !rtaStage.canUnlockDecision(this.rta, this.viewerOrg, this.viewerProfile)) {
        return Promise.resolve(null)
      }

      // A form whose last save was refused: no confirm for a command the flush would stop anyway. The flush says why (a 422 again in its
      // words, focused) or sends the values once more; the Unlock goes on only if it is taken.
      if (this.savesRefused()) {
        // The line is busy across the retry (`lockRunning`), so a second press cannot start a second one, or stack a second confirm.
        this.lockRunning = 'unlock'

        return this.flushAutosaves().then(function (ok) {
          self.lockRunning = null

          return ok && !self._isDestroyed ? self.unlockDecision() : null
        })
      }

      var copy = rtaDecision.unlockCopy(this.rta, { telco: this.telcoName, towerCo: this.towerCo, reference: this.reference })

      return approvalLock.askUnlock(this.$root, Object.assign({ alive: function () { return !self._isDestroyed } }, copy)).then(function (answer) {
        if (answer !== 'unlock') {
          self.focusDecisionLine('unlock')

          return { ok: false, status: 'cancelled' }
        }

        self.lockRunning = 'unlock'

        return self.flushAutosaves().then(function (ok) {
          if (!ok || self._isDestroyed) {
            self.lockRunning = null
            return null
          }

          return self.rtaCommand('post', '/actions/' + self.rta.id + '/rta/decision/unlock', {}, 'RTA').then(function (result) {
            self.lockRunning = null

            if (!result.ok) {
              self.refuseCommand(result, 'unlock')
              return result
            }

            self.takeAnswer(result.data)
            self.$message({ message: rtaDecision.sentence('unlock'), type: 'success' })

            // Back at Step 4 (the stage is Client Approved again); focus its title.
            self.goToStep(4)
            self.$nextTick(function () { self.focusStepHeading() })

            return result
          })
        })
      })
    },

    focusDecisionLine (which) {
      var self = this

      this.$nextTick(function () {
        var d = self.$refs.decision || self.$refs.completion

        if (d && typeof d.focusLine === 'function') {
          d.focusLine(which)
        }
      })
    },

    /* ---------------------------------------------------------------------
       Step 5's Complete RTA (B6, decisions 40, 45): flush, check, confirm, POST /rta/complete (API E19)
    --------------------------------------------------------------------- */

    /**
     The button, one click:
       1. flush Step 5's auto-save (the XOL-582 ordering rule): Complete reads what the server holds; a refused save stops here;
       2. judge the gate again on what the server now holds (another closer may have unlocked);
       3. the advisory precheck (site name and ID, the handover date and status, the RTS dates and priority): Step 5 marks the fields and the tabs and lists them
          above the forms, and nothing opens;
       4. the confirm opens. It warns when the decision was never e-mailed (decision 49).
     */
    completeRta () {
      var self = this
      var form = this.$refs.completion

      if (this.completing || this.completeConfirmOpen || this.completeBusy || !this.showComplete || !form) {
        return Promise.resolve(null)
      }

      this.completing = true

      return form.flush().then(function (r) {
        self.completing = false

        if (!r.ok || self._isDestroyed || !rtaStage.canComplete(self.rta, self.viewerOrg, self.viewerProfile)) {
          return null
        }

        if (!form.precheck().ok) {
          return null
        }

        self.completeSnap = self.withHandoverPast(form.snapshot())
        self.completeUnsent = rtaStage.decisionUnsent(self.rta, self.viewerOrg)
        self.completeRemarks = ''
        self.completeConfirmOpen = true

        return true
      })
    },

    // Decision 86: is the handover date today or earlier (with a status chosen)? Compared as 'YYYY-MM-DD' strings on the app's clock
    // (formatDateSql, appNowDateSql), never parsed (XOL-414). A kept site that has already moved on has neither (decision 108).
    withHandoverPast (snap) {
      snap.handoverPast = !snap.transitionedStatus &&
        rtaCompletion.handoverWarning(this.formatDateSql(snap.handover), snap.handoverStatus, appNowDateSql())

      return snap
    },

    cancelComplete () {
      if (this.completeBusy) {
        return
      }

      this.completeConfirmOpen = false
      this.focusCompleteButton()
    },

    // The button was loading (so disabled, so blurred) while the flush ran and the dialog opened after it: the dialog cannot hand focus back.
    focusCompleteButton () {
      this.focusButton('completeButton')
    },

    onCompleteRemarks (value) {
      this.completeRemarks = value
    },

    /**
     The confirm's action. Step 5 can have been edited while the confirm was open (a pause may end meanwhile): flush once more and judge
     again. A refused flush, or a gate that no longer allows Complete, closes the confirm with nothing sent. Then POST
     /actions/{id}/rta/complete { expectedApprovedStatus, expectedVersion, remarks?, expectedRtsChoice? }.
     */
    doComplete () {
      var self = this
      var form = this.$refs.completion

      if (this.completeBusy || !this.completeConfirmOpen || !form) {
        return Promise.resolve(null)
      }

      this.completeBusy = true

      return form.flush().then(function (r) {
        if (!r.ok || self._isDestroyed || !rtaStage.canComplete(self.rta, self.viewerOrg, self.viewerProfile)) {
          self.completeBusy = false
          self.completeConfirmOpen = false
          self.focusCompleteButton()

          return null
        }

        var body = { expectedApprovedStatus: 'Approved', expectedVersion: form.currentVersion() }
        var remarks = String(self.completeRemarks || '').trim()

        if (remarks) {
          body.remarks = remarks
        }

        var snap = self.withHandoverPast(form.snapshot())

        // XOL-693: the fingerprint of the client's RTS choice. It is the one the closer CONFIRMED (the snapshot taken when the confirm opened), not
        // the fresh one: if Step 5 was rebuilt while the confirm was open (the RTA was re-read), the fresh key is the new state and the API could
        // not refuse it, so Complete could do something other than what the confirm said. The API refuses a moved sharer or RTS state with 409
        // stale_rts_choice; with no choice the key is left out, and the API reads absent as none.
        var confirmedKey = self.completeSnap ? self.completeSnap.rtsChoiceKey : snap.rtsChoiceKey

        if (confirmedKey) {
          body.expectedRtsChoice = confirmedKey
        }
        var site = snap.siteName

        return self.rtaCommand('post', '/actions/' + self.rta.id + '/rta/complete', body, 'RTA').then(function (result) {
          self.completeBusy = false
          self.completeConfirmOpen = false

          if (!result.ok) {
            // Fields and the version are Step 5's to show (completion_invalid, site_name_taken, site_id_taken, stale_completion); the
            // rest comes back as `refused` (not_transitioned takes the drawer to Step 4).
            if (self.$refs.completion) {
              self.$refs.completion.refused(result, { command: 'complete' })
            } else {
              self.refuseCommand(result, 'complete')
            }

            // A stale RTS choice puts the closer on the RTS section (Step 5 does that), not on the button.
            if (!(result.failure && result.failure.code === 'stale_rts_choice')) {
              self.focusCompleteButton()
            }

            return result
          }

          self.takeAnswer(result.data)
          self.rtaToast(rtaCompletion.completeSentence({
            siteName: site,
            tcClient: self.tcClient,
            telco: self.telcoName,
            handoverDate: snap.handover ? self.formatDate(snap.handover) : '',
            handoverStatus: snap.handoverStatus,
            handoverPast: snap.handoverPast,
            transitionedStatus: snap.transitionedStatus,
            rtsOutcome: snap.rtsOutcome,
            rtsLink: snap.rtsLink
          }), result.data, { actor: 'towerco', orgs: [self.telcoName], towerCo: self.towerCo })

          // The button is gone with the RTA closed: focus goes to the Completed panel.
          self.$nextTick(function () { self.focusPanel() })

          return result
        })
      })
    },

    // A refusal of a Step 5 save or of Complete that is not about a field or the version: worded and acted on here (not_transitioned goes to Step 4;
    // response_changed, the approved option's unsubmitted review change, reads the server again and says whose it is).
    onCompletionRefused (result, opts) {
      this.refuseCommand(result, opts && opts.command ? opts.command : 'completion')
    },

    focusPanel () {
      var t = this.$el && typeof this.$el.querySelector === 'function' ? this.$el.querySelector('.rta-panel__title') : null

      if (t && typeof t.focus === 'function') {
        t.focus()
      } else {
        this.focusStepHeading()
      }
    },

    // The drawer's close guard (XOL-596): what the open form (Step 1's request, Step 4's decision or Step 5's entries) has waiting is saved before the drawer
    // closes; a refusal keeps it open (the notice says why). Page leave is the auto-save's own (a fetch keepalive request with the auth headers, DEV-18).
    leaveNeeded () {
      return this.hasPendingSaves()
    },

    leaveGuard () {
      return this.flushAutosaves()
    },

    /* ---------------------------------------------------------------------
       The options this drawer's maps draw
    --------------------------------------------------------------------- */

    // The options table read its rows: the Map draws their pins.
    onOptionsLoaded (rows) {
      this.optionRows = rows || []
    },

    // The RTA's option rows (50 a page, DEV-20), for the maps: Step 1 mounts no options table, and the Propose confirm lists the Draft ones as they are now.
    // Resolves with the rows (those on screen stay when the read fails).
    loadOptionRows () {
      var self = this
      var seq = ++this.rowsSeq

      if (!this.rta || !this.rta.id) {
        return Promise.resolve(this.optionRows)
      }

      return Promise.resolve(axios.get(this.getEndpoint('/candidates/rta/' + this.rta.id + '?per_page=50'))).then(function (response) {
        var body = response ? response.data : null
        var rows = Array.isArray(body) ? body : (body && Array.isArray(body.data) ? body.data : [])

        if (seq === self.rowsSeq && !self._isDestroyed) {
          self.optionRows = rows
        }

        return self.optionRows
      }, function (e) {
        console.error('XOL-67: could not read the options of RTA', self.rta && self.rta.id, e)

        return self.optionRows
      })
    },

    /**
     A command somewhere (an option's layer, the table's delete, the opener) changed the RTA. The layer sends the RTA as it is now
     (the command's `action`); with none, the RTA is read again. The row-sync keys stay as they are: `emails` and the versions
     belong to the per-field save (Emails Update), which is registered against the object loaded first.
     */
    onRtaChanged (event) {
      if (!event || !this.rta || (event.id && Number(event.id) !== Number(this.rta.id))) {
        return
      }

      if (event.action) {
        this.adoptAction(event.action)
      } else {
        this.fetchData('/actions/' + this.rta.id, this.adoptAction)
      }
    },

    // An option layer asks which RTA this drawer holds: it answers with the object it shows (not a copy), so the layer follows
    // it as it changes (the Send E-mails switch of the banner included).
    onDrawerRequest (event) {
      if (event && this.rta && Number(event.id) === Number(this.rta.id)) {
        this.$events.emit(RTA_DRAWER, { id: this.rta.id, action: this.actionData })
      }
    },

    adoptAction (action) {
      var self = this

      if (!action || typeof action !== 'object' || this._isDestroyed || !this.actionData) {
        return
      }

      var moved = false

      Object.keys(action).forEach(function (k) {
        if (k !== 'emails' && k !== 'contentVersion' && k !== 'fieldVersions') {
          if (listSignature(k, self.actionData[k]) !== listSignature(k, action[k])) {
            moved = true
          }

          self.$set(self.actionData, k, action[k])
        }
      })

      // A command asked to move on once the RTA shows its new stage (followStep). The FIRST reading after it settles that, whatever stage it shows: a much
      // later reading must never move the drawer.
      if (this.pendingStep !== null) {
        var wanted = this.pendingStep

        this.pendingStep = null

        if (this.openable(wanted)) {
          this.followStep(wanted)
        }
      }

      // The versions are not copied into the action (see onRtaChanged); Step 1 reads this one.
      if (typeof action.contentVersion === 'number' && (this.rowVersion === null || action.contentVersion > this.rowVersion)) {
        this.rowVersion = action.contentVersion
      }

      // Only a change the list can show refreshes it: the decision e-mail's batch settling (Step 4 reads the RTA every few seconds while it
      // is "Sending") moves nothing in the list, and must not reload it each time.
      if (moved) {
        this.scheduleListRefresh()
      }
    },

    // The Acquire list (and the Needs My Action count it reads after every load) follows any change of the RTA made here (XOL-67 B10:
    // a command, an option layer's answer, a re-read). One refresh after the changes settle, so a burst of auto-saves is one read. Not in a
    // site drawer's record (no list is behind it). Closing the drawer refreshes it too (RequestDetails.beforeDestroy).
    scheduleListRefresh () {
      var self = this

      if (this.embedded || this._isDestroyed) {
        return
      }

      if (this._listTimer) {
        clearTimeout(this._listTimer)
      }

      this._listTimer = setTimeout(function () {
        self._listTimer = null
        self.$events.emit(RTA_LIST_REFRESH)
      }, LIST_REFRESH_MS)
    },

    // The button the user pressed may be gone (Previous at Step 2) or disabled (Next at the last step): focus
    // moves to the step's title instead of falling to the page.
    keepFocus () {
      var self = this

      this.$nextTick(function () {
        var active = typeof document !== 'undefined' ? document.activeElement : null

        if (!active || active === document.body || !self.$el.contains(active) || active.disabled) {
          self.focusStepHeading()
        }
      })
    },

    focusStepHeading () {
      var h = this.$refs.stepHeading

      if (h && typeof h.focus === 'function') {
        h.focus()
      }
    },

    // The Send E-mails switch of the banner. Element 2.15's ElSwitch writes `$refs.input.checked` in a
    // nextTick after @change, so the banner (and the switch in it) must leave the DOM AFTER that tick, or
    // it throws: the change is applied one macrotask later.
    onMailSwitch (on) {
      var self = this

      setTimeout(function () {
        self.applyMailSwitch(on)
      }, 0)
    },

    // The same three steps as EmailSetup.onSendMailChange: set it, mark the field, save it at once.
    applyMailSwitch (on) {
      var row = this.actionData

      if (this._isDestroyed || !row || !row.emails) {
        return Promise.resolve(null)
      }

      this.$set(row.emails, 'sendMail', on)
      this.fieldChanged(row, 'emails.sendMail')

      return this.flushFieldPaths([{ row: row, path: 'emails.sendMail' }])
    },

    // The Activity popup opened. The FIRST open has nothing to reload (the body renders once shown); every
    // later open reads page 1 again.
    reloadActivity () {
      var log = this.$refs.activity

      if (log && typeof log.reload === 'function') {
        log.reload()
      }
    },

    // The recipient lists are live: every open of Emails Update re-reads them.
    reloadEmailSetup () {
      var setup = this.$refs.emailSetup

      if (setup && typeof setup.reload === 'function') {
        setup.reload()
      }
    },

    // Completed: the site and the RTS, one layer up the stack (revealDrawer, so a drawer of that kind already
    // open underneath is put on top rather than switched in place).
    viewSite () {
      if (this.$router && this.siteLinkId) {
        revealDrawer(this.$router, 'site', { id: this.siteLinkId })
      }
    },

    viewRts () {
      if (this.$router && this.rtsLinkId) {
        revealDrawer(this.$router, 'request', { id: this.rtsLinkId })
      }
    }

  },


  created () {
    // Not reactive: which read of the options is the latest, and the step a command is waiting to move to (followStep).
    this.rowsSeq = 0
    this.pendingStep = null

    this.viewStep = this.initialStep()
    // Before the first render, so the Map is drawn into an open panel (not one that opens a tick later).
    this.openPanels = this.panelsFor(this.viewStep)

    // One registered object per row, so the first emails.* change is saved against what was loaded
    // (RequestDetails registers it too; this attaches this drawer's form scope).
    if (this.actionData) {
      this.syncRegisterRow(this.actionData, 'action')
    }
  },


  mounted () {
    // Native typing anywhere in the drawer (the Emails Update popup included) restarts the pause.
    sync.attachScopeRoot(this.syncScope, this.$el)

    // XOL-67 (B4): the option layer and the Options table tell this drawer the RTA moved.
    this.$events.on(RTA_CHANGED, this.onRtaChanged)
    this.$events.on(RTA_DRAWER_REQUEST, this.onDrawerRequest)

    // XOL-67 (B5): closing the drawer waits for the open form's auto-save. (The `emails.*` rows stay with the global navigation guard:
    // this drawer does not claim them with setScopeGuarded.)
    this._leaveGuard = this.leaveGuard.bind(this)
    this._leaveNeeded = this.leaveNeeded.bind(this)

    if (this.drawerDirtyRegistry && typeof this.drawerDirtyRegistry.registerLeaveGuard === 'function') {
      this.drawerDirtyRegistry.registerLeaveGuard(this._leaveGuard, this._leaveNeeded)
    }

    // Step 1's map draws the options' pins, and no options table is there to read them. (Steps 2 and 3 have it: its `loaded` gives the rows.)
    if (this.viewStep === 1 || (this.resetOptionId && this.viewStep >= 4)) {
      this.loadOptionRows()
    }
  },


  beforeDestroy () {
    this.$events.off(RTA_CHANGED, this.onRtaChanged)
    this.$events.off(RTA_DRAWER_REQUEST, this.onDrawerRequest)

    // A read of the options still on its way is not wanted any more.
    this.rowsSeq++

    // A refresh still waiting is the drawer's close to make now (RequestDetails.beforeDestroy emits the same event; this covers the drawer
    // used without it).
    if (this._listTimer) {
      clearTimeout(this._listTimer)
      this._listTimer = null
    }

    if (this.drawerDirtyRegistry && typeof this.drawerDirtyRegistry.unregisterLeaveGuard === 'function') {
      this.drawerDirtyRegistry.unregisterLeaveGuard(this._leaveGuard)
    }

    // The safety flush: stop the timer and save whatever changed now.
    sync.destroyScope(this.syncScope)
  }

}

</script>


<style scoped>
  /* The Activity and Emails Update links: right-aligned on one line (RequestWorkflow.vue). */
  .request-footer-links {display: flex; justify-content: flex-end; align-items: center; flex-wrap: wrap;}
  .request-footer-links > div {margin-left: 16px;}

  .rta-footer {margin-top: 12px;}

  .rta-cue {margin: 6px 0 4px; text-align: center;}
  .rta-cue .el-tag {margin: 0 4px;}
  .rta-cue__line {display: inline-block; margin-left: 4px;}

  .rta-stepline {margin-top: 8px;}
  .rta-stepline h4 {margin-bottom: 2px;}
  /* The step title takes focus (tabindex -1) when the button that was pressed has gone: a ring for the keyboard, none after a click. */
  .rta-stepline h4:focus {outline: none;}
  .rta-stepline h4:focus-visible {outline: 2px solid #409EFF; outline-offset: 2px;}

  .rta-panel {margin-bottom: 12px;}
  .rta-panel__title:focus {outline: none;}
  .rta-panel__title:focus-visible {outline: 2px solid #409EFF; outline-offset: 2px;}
  .rta-panel__description {margin: 2px 0 0;}
  .rta-panel__actions {margin-top: 8px;}

  /* Steps 2 and 3. */
  .rta-collapse {margin-top: 16px;}

  /* The Propose, Submit decision and Reject RTA… confirms (UX spec 4.6). */
  .rta-confirm-list {margin: 8px 0 12px; padding-left: 20px; line-height: 1.5;}
  .rta-confirm-warning {margin: 8px 0 12px;}
  .rta-confirm-handover {margin-bottom: 8px;}

  /* The step footer (B12, decision 130), RequestWorkflow's .workflow-footer: Previous Step | the step's action | Next Step. The side
     cells are equal (flex 1 1 0) so the action sits in the middle of the drawer at every step; they keep min-width: auto, so a wide
     middle wraps its hint instead of the side cells collapsing. The side buttons are Element text buttons, as the old row's were.
     Decision 189: a hairline above it, and the hints are 12px #606266 (7:1 on white), not the 16px text-muted. */
  .rta-stepfoot {display: flex; align-items: flex-start; gap: 24px; margin-top: 24px; padding: 16px 0 12px; border-top: 1px solid #EBEEF5;}
  .rta-stepfoot__prev,
  .rta-stepfoot__next {flex: 1 1 0;}
  .rta-stepfoot__prev {text-align: left;}
  .rta-stepfoot__next {text-align: right;}
  .rta-stepfoot__action {flex: 0 1 auto; min-width: 0;}
  .rta-stepfoot__row {display: flex; flex-direction: column; align-items: center; gap: 6px; text-align: center; max-width: 280px;}
  .rta-stepfoot__hint {display: block; font-size: 12px; line-height: 1.5; color: #606266; max-width: 260px;}
  /* The middle cell: the step's own act first, then Reject RTA…, side by side, each with its hint under it (UX spec 2.2.6). */
  .rta-stepfoot__pair {display: flex; align-items: flex-start; justify-content: center; flex-wrap: wrap; gap: 16px 32px;}
  /* Back to Searching sits in the left cell: left-aligned, as Previous Step is. */
  .rta-stepfoot__prev .rta-stepfoot__row {align-items: flex-start; text-align: left;}
  .rta-stepfoot .el-button:focus-visible {outline: 2px solid #409EFF; outline-offset: 2px;}
  /* Room between a FontAwesome icon and the words (Element spaces only its own el-icon-* icons): the rta-view-btn pattern, here for every footer button. */
  .rta-stepfoot >>> .el-button .fa + span {margin-left: 6px;}

  /* xs / sm / md (vue-mq keys are upper bounds: a 375px phone is sm): Previous, the action, then Next, one under the other; an empty
     cell is not drawn. The acts and Back to Searching are full width with the hint centred under them; Previous and Next are text
     buttons and stay at their own width (left and right). */
  .rta-stepfoot--stacked {flex-direction: column; align-items: stretch; gap: 12px; margin-top: 16px; padding-top: 12px;}
  .rta-stepfoot--stacked .rta-stepfoot__prev,
  .rta-stepfoot--stacked .rta-stepfoot__action,
  .rta-stepfoot--stacked .rta-stepfoot__next {flex: 0 0 auto;}
  .rta-stepfoot--stacked .rta-stepfoot__prev--empty,
  .rta-stepfoot--stacked .rta-stepfoot__action--empty {display: none;}
  .rta-stepfoot--stacked .rta-stepfoot__pair {flex-direction: column; align-items: stretch; gap: 12px;}
  .rta-stepfoot--stacked .rta-stepfoot__row {align-items: stretch; max-width: none;}
  .rta-stepfoot--stacked .rta-stepfoot__row .el-button {width: 100%;}
  .rta-stepfoot--stacked .rta-stepfoot__hint {max-width: none;}

  /* Spoken, not seen (the repo has no global sr-only class). */
  .rta-visually-hidden {position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap;}

/* Carlos 2026-10-29: room between the chevron and the text (Element spaces only its own el-icon-* icons, not FontAwesome ones). */
.rta-view-btn >>> .fa + span {margin-left: 6px;}
</style>
